/**
 * AdminPlatformApi — domain prompt templates, produced assets, AI cost
 * dashboard, MCP usage dashboard, admin projects (CUR-530),
 * and admin inbound email (CUR-1052) — the super-admin platform surface.
 *
 * Extracted from client.ts (CUR-547 god-object split, batch 2). Methods-only
 * by design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type { DomainPromptHistoryResponse, DomainPromptTemplate, DomainPromptsResponse, UpdateDomainPromptRequest, UpdateDomainPromptResponse } from '../../../../shared/types/api';
import { APIError as SharedAPIError, buildApiUrl, createAuthHeaders, processApiResponse } from '../../../../shared/utils/api-helpers';
import { API_ENDPOINTS } from '@/utils/env';
import { transformKeysToCamelCase } from '@/utils/transform-keys';

import { BaseApiClient } from './base';

// ============================================================================
// Admin Projects types (CUR-530)
// ============================================================================

export interface AdminProjectsListParams {
  organizationId?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AdminProjectListItem {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  name: string;
  slug: string;
  status: string;
  domains: string[];
  sourceCount: number;
  contentCount: number;
  memberCount: number;
  ownerEmail: string | null;
  createdAt: string | null;
}

export interface AdminProjectsListResponse {
  items: AdminProjectListItem[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface AdminProjectSourceItem {
  id: string;
  name: string;
  platformKey: string;
  handle: string | null;
  status: string;
  syncEnabled: boolean;
  contentCount: number;
  lastSyncAt: string | null;
  lastContentAt: string | null;
}

export interface AdminProjectFailureItem {
  contentId: string;
  title: string | null;
  contentType: string | null;
  sourceName: string | null;
  failedAt: string | null;
}

export interface AdminProjectContentStats {
  total: number;
  completed: number;
  failed: number;
  processing: number;
  pending: number;
}

export interface AdminProjectDetailResponse {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  name: string;
  slug: string;
  description: string | null;
  brief: string | null;
  domains: string[];
  status: string;
  maxSources: number;
  sourceCount: number;
  contentCount: number;
  memberCount: number;
  ownerEmail: string | null;
  createdAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  sources: AdminProjectSourceItem[];
  contentStats: AdminProjectContentStats;
  recentFailures: AdminProjectFailureItem[];
}

// ============================================================================
// Cost Dashboard Types
// ============================================================================

export interface CostSummaryResponse {
  currentMonthTotalUsd: number;
  previousMonthTotalUsd: number;
  trendPercent: number;
  avgDailyCost: number;
  period: string;
  daysElapsed: number;
  cached: boolean;
}

export interface DailyCostPoint {
  date: string;
  totalCostUsd: number;
  inputTokens: number;
  outputTokens: number;
  countTraces: number;
}

export interface CostsDailyResponse {
  days: DailyCostPoint[];
  periodDays: number;
  cached: boolean;
}

export interface ModelCost {
  model: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  pctOfTotal: number;
}

export interface CostsByModelResponse {
  models: ModelCost[];
  totalCostUsd: number;
  observationsScanned: number;
  periodDays: number;
  cached: boolean;
}

export interface ContentCost {
  contentId: string;
  title: string;
  contentType: string;
  platform: string;
  displayType: string;
  durationSeconds: number | null;
  slideCount: number | null;
  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  callCount: number;
  modelsUsed: string[];
  agentsUsed: string[];
  pctOfTotal: number;
}

export interface CostsByContentResponse {
  items: ContentCost[];
  totalCostUsd: number;
  periodDays: number;
  cached: boolean;
}

// ============================================================================
// MCP Usage Dashboard Types
// ============================================================================

export interface McpUsageSummaryResponse {
  totalCalls: number;
  errorCount: number;
  uniqueTools: number;
  avgCallsPerDay: number;
  activeOrgs: number;
  periodDays: number;
}

export interface McpDailyPoint {
  date: string;
  callCount: number;
  errorCount: number;
}

export interface McpUsageDailyResponse {
  days: McpDailyPoint[];
  periodDays: number;
}

export interface McpToolUsage {
  toolName: string;
  callCount: number;
  errorCount: number;
  pctOfTotal: number;
}

export interface McpUsageByToolResponse {
  tools: McpToolUsage[];
  totalCalls: number;
  periodDays: number;
}

// ============================================================================
// Admin Inbound Email types (CUR-1052 PR-4, super-admin only)
// ============================================================================

export interface AdminInboundAddressEntry {
  tokenId: string;
  projectId: string;
  projectName: string | null;
  organizationId: string | null;
  organizationName: string | null;
  address: string;
  isActive: boolean;
  createdAt: string;
  revokedAt: string | null;
}

export interface AdminInboundAddressListResponse {
  entries: AdminInboundAddressEntry[];
  totalCount: number;
}

export interface AdminInboundLogEntry {
  id: string;
  projectId: string | null;
  projectName: string | null;
  organizationName: string | null;
  fromAddress: string | null;
  subject: string | null;
  status: string;
  detail: string | null;
  resendEmailId: string | null;
  receivedAt: string | null;
  contentId: string | null;
  createdAt: string;
}

export interface AdminInboundLogResponse {
  entries: AdminInboundLogEntry[];
  totalCount: number;
}

export interface AdminInboundLogParams {
  projectId?: string;
  status?: string;
  limit?: number;
}

export class AdminPlatformApi extends BaseApiClient {
  // ============================================================================
  // Domain Prompt Templates (Multi-Domain System)
  // ============================================================================

  /**
   * Get all 3 prompts for a specific domain
   */
  async getDomainPrompts(domain: string): Promise<DomainPromptsResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.domainPrompts(domain));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      headers,
    });

    const result = await processApiResponse<DomainPromptsResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get a specific prompt for a domain and agent type
   */
  async getDomainPrompt(domain: string, agentType: string): Promise<DomainPromptTemplate> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.domainPrompt(domain, agentType));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      headers,
    });

    const result = await processApiResponse<DomainPromptTemplate>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get version history for a specific domain prompt
   */
  async getDomainPromptHistory(domain: string, agentType: string): Promise<DomainPromptHistoryResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.domainPromptHistory(domain, agentType));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      headers,
    });

    const result = await processApiResponse<DomainPromptHistoryResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Update a domain prompt (creates new version)
   */
  async updateDomainPrompt(
    domain: string,
    agentType: string,
    data: UpdateDomainPromptRequest
  ): Promise<UpdateDomainPromptResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.domainPrompt(domain, agentType));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await processApiResponse<UpdateDomainPromptResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Produced Assets
  // ============================================================================

  async getAssets(params?: {
    status?: string;
    asset_type?: string;
    source_content_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.asset_type) searchParams.set('asset_type', params.asset_type);
    if (params?.source_content_id) searchParams.set('source_content_id', params.source_content_id);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset != null) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    const url = buildApiUrl(this.baseUrl, `/api/assets${query ? `?${query}` : ''}`);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  async getAsset(id: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/api/assets/${id}`);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  async createAsset(data: unknown): Promise<any> {
    const url = buildApiUrl(this.baseUrl, '/api/assets');
    const headers = {
      ...createAuthHeaders(await this.resolveToken()),
      'Content-Type': 'application/json',
    };
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  async deleteAsset(id: string): Promise<void> {
    const url = buildApiUrl(this.baseUrl, `/api/assets/${id}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new SharedAPIError(response.status, text || `Failed to delete asset ${id}`);
    }
  }

  async updateAssetStatus(id: string, status: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/api/assets/${id}`);
    const headers = {
      ...createAuthHeaders(await this.resolveToken()),
      'Content-Type': 'application/json',
    };
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ status }),
    });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  async publishAsset(id: string, options?: { platform?: string; dry_run?: boolean }): Promise<{
    task_id: string;
    asset_id: string;
    platform: string;
    dry_run: boolean;
    status: string;
  }> {
    const url = buildApiUrl(this.baseUrl, `/api/assets/${id}/publish`);
    const headers = {
      ...createAuthHeaders(await this.resolveToken()),
      'Content-Type': 'application/json',
    };
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        platform: options?.platform ?? 'instagram',
        dry_run: options?.dry_run ?? false,
      }),
    });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  async getPublishStatus(id: string, taskId: string): Promise<{
    task_id: string | null;
    asset_id: string;
    asset_status: string;
    task_status: string | null;
    task_result: Record<string, unknown> | null;
  }> {
    const url = buildApiUrl(this.baseUrl, `/api/assets/${id}/publish/status?task_id=${encodeURIComponent(taskId)}`);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  // ============================================================================
  // AI Cost Dashboard Methods
  // ============================================================================

  async getCostSummary(): Promise<CostSummaryResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminCostsSummary);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<CostSummaryResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<CostSummaryResponse>(result.data);
  }

  async getCostsDaily(days: number = 30): Promise<CostsDailyResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminCostsDaily}?days=${days}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<CostsDailyResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<CostsDailyResponse>(result.data);
  }

  async getCostsByModel(days: number = 30): Promise<CostsByModelResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminCostsByModel}?days=${days}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<CostsByModelResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<CostsByModelResponse>(result.data);
  }

  async getCostsByContent(days: number = 30, limit: number = 50): Promise<CostsByContentResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminCostsByContent}?days=${days}&limit=${limit}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<CostsByContentResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<CostsByContentResponse>(result.data);
  }

  // ============================================================================
  // MCP Usage Dashboard Methods
  // ============================================================================

  async getMcpUsageSummary(days: number = 30): Promise<McpUsageSummaryResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminMcpUsageSummary}?days=${days}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<McpUsageSummaryResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<McpUsageSummaryResponse>(result.data);
  }

  async getMcpUsageDaily(days: number = 30): Promise<McpUsageDailyResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminMcpUsageDaily}?days=${days}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<McpUsageDailyResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<McpUsageDailyResponse>(result.data);
  }

  async getMcpUsageByTool(days: number = 30): Promise<McpUsageByToolResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminMcpUsageByTool}?days=${days}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<McpUsageByToolResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<McpUsageByToolResponse>(result.data);
  }

  // ============================================================================
  // Admin Projects (CUR-530, super-admin only — Pattern B)
  // ============================================================================

  async getAdminProjects(
    params: AdminProjectsListParams = {},
  ): Promise<AdminProjectsListResponse> {
    const search = new URLSearchParams();
    if (params.organizationId) search.set('organization_id', params.organizationId);
    if (params.status) search.set('status', params.status);
    if (params.search) search.set('search', params.search);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));

    const qs = search.toString();
    const path = qs ? `${API_ENDPOINTS.adminProjects}?${qs}` : API_ENDPOINTS.adminProjects;
    const url = buildApiUrl(this.baseUrl, path);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<AdminProjectsListResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<AdminProjectsListResponse>(result.data);
  }

  async getAdminProject(projectId: string): Promise<AdminProjectDetailResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminProject(projectId));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<AdminProjectDetailResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<AdminProjectDetailResponse>(result.data);
  }


  // ============================================================================
  // Admin Inbound Email (CUR-1052 PR-4 — super-admin only, read-only)
  // ============================================================================

  async getAdminInboundAddresses(
    params: { includeRevoked?: boolean; limit?: number } = {},
  ): Promise<AdminInboundAddressListResponse> {
    const search = new URLSearchParams();
    if (params.includeRevoked) search.set('include_revoked', 'true');
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    const path = qs
      ? `${API_ENDPOINTS.adminInboundAddresses}?${qs}`
      : API_ENDPOINTS.adminInboundAddresses;
    const url = buildApiUrl(this.baseUrl, path);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<AdminInboundAddressListResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return transformKeysToCamelCase<AdminInboundAddressListResponse>(result.data);
  }

  async getAdminInboundLog(
    params: AdminInboundLogParams = {},
  ): Promise<AdminInboundLogResponse> {
    const search = new URLSearchParams();
    if (params.projectId) search.set('project_id', params.projectId);
    if (params.status) search.set('status', params.status);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    const path = qs
      ? `${API_ENDPOINTS.adminInboundLog}?${qs}`
      : API_ENDPOINTS.adminInboundLog;
    const url = buildApiUrl(this.baseUrl, path);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<AdminInboundLogResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return transformKeysToCamelCase<AdminInboundLogResponse>(result.data);
  }
}
